<template>
  <div class="shipment-progress-indicators">
    <ShipmentTrackingProgressBar
      :label="t('enumMap.ShipmentStatus.BOOKED')"
      :in-progress="isCarrierAssigned"
      :complete="atPickup || isBooked || inTransit || atDelivery || delivered"
    />
    <ShipmentTrackingProgressBar
      :label="t('shipments.trackingProgress.atPickup')"
      :in-progress="atPickup"
      :complete="inTransit || atDelivery || delivered"
    />
    <ShipmentTrackingProgressBar
      :label="t('shipments.trackingProgress.inTransit')"
      :in-progress="inTransit"
      :complete="atDelivery || delivered"
    />
    <ShipmentTrackingProgressBar
      :label="t('shipments.trackingProgress.atDelivery')"
      :in-progress="atDelivery"
      :complete="atDelivery || delivered"
    />
    <ShipmentTrackingProgressBar
      :label="t('shipments.trackingProgress.delivered')"
      :in-progress="delivered"
      :complete="delivered"
    />
  </div>
</template>

<script lang="ts" setup>
import type { PropType } from 'vue';
import { computed, toRefs } from 'vue';
import { useI18n } from 'vue-i18n';

import { Shipment, ShipmentStatus, ShipmentTrackingSummary } from '@/model';
import { isStatusEqualOrAfter } from '@/util';

import ShipmentTrackingProgressBar from '@/components/shipment/tracking/ShipmentTrackingProgressBar.vue';

const props = defineProps({
  shipment: {
    type: Object as PropType<Shipment | ShipmentTrackingSummary>,
    default: () => new Shipment(),
  },
});
const { shipment } = toRefs(props);

const { t } = useI18n();

const status = computed(() => shipment.value.status ?? ShipmentStatus.PLANNING);

const origin = computed(() => shipment.value.origin);

const destination = computed(() => shipment.value.destination);

const isCanceled = computed(() => status.value === ShipmentStatus.CANCELED);

const isCarrierAssigned = computed(() => {
  return (
    !isCanceled.value &&
    isStatusEqualOrAfter(status.value, ShipmentStatus.CARRIER_ASSIGNED)
  );
});

const isBooked = computed(() => {
  return (
    !isCanceled.value &&
    isStatusEqualOrAfter(status.value, ShipmentStatus.BOOKED)
  );
});

// The priority is showing something coherent to carriers. These properties
// are intentionally over broad to accomodate possible bad data
const atPickup = computed(() => {
  return !!origin.value.actualArrival || !!origin.value.actualDeparture;
});

const atDelivery = computed(() => {
  return (
    !!destination.value.actualArrival || !!destination.value.actualDeparture
  );
});

const delivered = computed(() => !!destination.value.actualDeparture);

const inTransit = computed(() => !!origin.value.actualDeparture);
</script>

<style>
.shipment-progress-indicators {
  display: flex;
  gap: var(--gap-2);
}
</style>
